import { useState, useMemo, useCallback, useEffect } from "react";
import { Search, ShieldCheck, Info, Database, Calculator } from "lucide-react";
import { cotizadorData, COTIZADOR_META } from "@/mocks/cotizador_data";
import { formatCLP, formatNum } from "@/lib/constants";
import { useFilters } from "@/context/FiltersContext";
import { trackEstimadorQuery } from "@/lib/analytics";
import SectionCard from "@/components/SectionCard";
import BarChart from "@/components/charts/BarChart";

const AMBITOS = [...new Set(cotizadorData.map((d) => d.ambito))].sort();

export default function Estimador() {
  const { filters } = useFilters();
  const [ambito, setAmbito] = useState("");
  const [prestacionCod, setPrestacionCod] = useState("");
  const [isapre, setIsapre] = useState("");
  const [seguroComp, setSeguroComp] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // Prestaciones filtered by ambito
  const prestacionesDisponibles = useMemo(() => {
    const filtered = ambito
      ? cotizadorData.filter((d) => d.ambito === ambito)
      : cotizadorData;
    const map = {};
    for (const d of filtered) {
      if (!map[d.cod]) map[d.cod] = { cod: d.cod, nombre: d.nombre, n: 0 };
      map[d.cod].n += d.n_total;
    }
    return Object.values(map).sort((a, b) => b.n - a.n);
  }, [ambito]);

  // ISAPREs available for selected prestacion
  const isapresDisponibles = useMemo(() => {
    if (!prestacionCod) return [];
    const filtered = cotizadorData.filter((d) => d.cod === prestacionCod);
    return [...new Set(filtered.map((d) => d.isapre))].filter(Boolean).sort();
  }, [prestacionCod]);

  // Search filter
  const prestacionesFiltradas = useMemo(() => {
    if (!searchQuery) return prestacionesDisponibles.slice(0, 50);
    const q = searchQuery.toLowerCase();
    return prestacionesDisponibles
      .filter((p) => p.nombre.toLowerCase().includes(q) || p.cod.toLowerCase().includes(q))
      .slice(0, 30);
  }, [prestacionesDisponibles, searchQuery]);

  // Result
  const resultado = useMemo(() => {
    if (!prestacionCod || !isapre) return null;
    return cotizadorData.find((d) => d.cod === prestacionCod && d.isapre === isapre) || null;
  }, [prestacionCod, isapre]);

  // Track query
  useEffect(() => {
    if (!resultado) return;
    trackEstimadorQuery({
      ambito,
      prestacion_cod: resultado.cod,
      prestacion_nombre: resultado.nombre,
      isapre: resultado.isapre,
      seguro_comp: seguroComp,
      p50: resultado.p50,
      n_observaciones: resultado.n_total,
      pct_copago_0: resultado.pct_copago_0,
    });
  }, [resultado, ambito, seguroComp]);

  const selectPrestacion = useCallback((p) => {
    setPrestacionCod(p.cod);
    setSearchQuery(p.nombre);
    setSearchOpen(false);
    setIsapre("");
  }, []);

  const handleAmbitoChange = (e) => {
    setAmbito(e.target.value);
    setPrestacionCod("");
    setSearchQuery("");
    setIsapre("");
  };

  const handleLimpiar = () => {
    setAmbito("");
    setPrestacionCod("");
    setSearchQuery("");
    setIsapre("");
    setSeguroComp(false);
    setSearchOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="estimador-page">
      {/* Hero */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Cotizador de Costo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Versión completa con contexto del pipeline · Datos reales IMED · {COTIZADOR_META.periodo} · {formatNum(COTIZADOR_META.total_observaciones)} bonos
        </p>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <span>✓</span>
          <span>Datos reales IMED · {formatNum(COTIZADOR_META.prestaciones_unicas)} prestaciones · {COTIZADOR_META.isapres_unicas} ISAPREs</span>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <span>✓</span>
          <span>Últimos 12 meses · Fórmula: copago = precio − bonif − seg_compl − dif_tarif</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Filtros */}
        <div className="lg:col-span-1 space-y-4">
          <SectionCard title="Buscar prestación" subtitle="Seleccione ámbito, prestación e ISAPRE">
            <div className="space-y-4">
              {/* Ámbito */}
              <div className="space-y-1.5">
                <label htmlFor="est-ambito" className="text-xs font-medium text-slate-600">Ámbito</label>
                <select
                  id="est-ambito"
                  value={ambito}
                  onChange={handleAmbitoChange}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 bg-white"
                >
                  <option value="">Todos los ámbitos</option>
                  {AMBITOS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Prestación */}
              <div className="space-y-1.5">
                <label htmlFor="est-prestacion" className="text-xs font-medium text-slate-600">Prestación</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="est-prestacion"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); setPrestacionCod(""); setIsapre(""); }}
                    onFocus={() => setSearchOpen(true)}
                    placeholder="Buscar por nombre o código..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                  />
                  {searchOpen && prestacionesFiltradas.length > 0 && (
                    <div className="absolute z-50 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                      {prestacionesFiltradas.map((p) => (
                        <button
                          key={p.cod}
                          type="button"
                          onClick={() => selectPrestacion(p)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-slate-50 last:border-0"
                        >
                          <span className="font-medium text-slate-800 text-xs">{p.nombre}</span>
                          <span className="ml-2 text-[10px] text-slate-400">{p.cod} · {formatNum(p.n)} obs</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ISAPRE */}
              <div className="space-y-1.5">
                <label htmlFor="est-isapre" className="text-xs font-medium text-slate-600">ISAPRE / Previsión</label>
                <select
                  id="est-isapre"
                  value={isapre}
                  onChange={(e) => setIsapre(e.target.value)}
                  disabled={!prestacionCod}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 bg-white disabled:opacity-50"
                >
                  <option value="">{prestacionCod ? "Seleccionar ISAPRE..." : "Primero seleccione prestación"}</option>
                  {isapresDisponibles.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>

              {/* Seguro complementario */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="est-seguro"
                  checked={seguroComp}
                  onChange={(e) => setSeguroComp(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                />
                <label htmlFor="est-seguro" className="text-xs text-slate-700 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                  Tiene seguro complementario
                </label>
              </div>

              {/* Limpiar */}
              <button
                type="button"
                onClick={handleLimpiar}
                className="w-full mt-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition"
              >
                Limpiar
              </button>
            </div>
          </SectionCard>

          {/* Metadata */}
          <SectionCard>
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-slate-400" />
              <h3 className="text-xs font-semibold text-slate-700">Sobre los datos</h3>
            </div>
            <div className="space-y-1.5 text-[11px] text-slate-500">
              <p><strong className="text-slate-700 tabular-nums">{formatNum(COTIZADOR_META.total_observaciones)}</strong> bonos analizados</p>
              <p><strong className="text-slate-700 tabular-nums">{formatNum(COTIZADOR_META.prestaciones_unicas)}</strong> prestaciones únicas</p>
              <p><strong className="text-slate-700 tabular-nums">{COTIZADOR_META.isapres_unicas}</strong> ISAPREs/previsiones</p>
              <p>Período: <strong className="text-slate-700">{COTIZADOR_META.periodo}</strong></p>
              <p>Fuente: <span className="text-slate-600 text-[10px]">IMED Bonos Electrónicos</span></p>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[10px] text-slate-400 leading-relaxed">
                <strong>Fórmula:</strong> copago = precio_clinica − bonificacion_isapre − seguro_complementario − diferencia_tarifaria
              </p>
            </div>
          </SectionCard>
        </div>

        {/* Resultado */}
        <div className="lg:col-span-2 space-y-6">
          {!resultado && (
            <SectionCard>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white shadow-lg mb-4">
                  <Calculator className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Seleccione una prestación e ISAPRE</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-sm">
                  Elija el ámbito, busque la prestación y seleccione su ISAPRE para ver la estimación de copago basada en datos reales.
                </p>
              </div>
            </SectionCard>
          )}

          {resultado && (
            <>
              {/* Copago principal */}
              <SectionCard
                title="Estimación de Copago"
                subtitle={<>Basado en <strong className="tabular-nums">{formatNum(resultado.n_total)}</strong> observaciones reales</>}
              >
                {/* Range bar */}
                <div className="mt-4 mb-4">
                  <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                    <span>P10</span>
                    <span>P25</span>
                    <span>P50 (mediana)</span>
                    <span>P75</span>
                    <span>P90</span>
                  </div>
                  <div className="relative h-8 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="absolute h-full bg-violet-200 rounded-full"
                      style={{ left: `${resultado.p90 > 0 ? (resultado.p10 / resultado.p90) * 100 : 0}%`, width: `${resultado.p90 > 0 ? ((resultado.p90 - resultado.p10) / resultado.p90) * 100 : 0}%` }}
                    />
                    <div
                      className="absolute h-full bg-violet-400 rounded-full"
                      style={{ left: `${resultado.p90 > 0 ? (resultado.p25 / resultado.p90) * 100 : 0}%`, width: `${resultado.p90 > 0 ? ((resultado.p75 - resultado.p25) / resultado.p90) * 100 : 0}%` }}
                    />
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-violet-700"
                      style={{ left: `${resultado.p90 > 0 ? (resultado.p50 / resultado.p90) * 100 : 50}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs font-semibold text-slate-700 mt-1 tabular-nums">
                    <span>{formatCLP(resultado.p10)}</span>
                    <span>{formatCLP(resultado.p25)}</span>
                    <span className="text-violet-700">{formatCLP(resultado.p50)}</span>
                    <span>{formatCLP(resultado.p75)}</span>
                    <span>{formatCLP(resultado.p90)}</span>
                  </div>
                </div>

                {/* 80% text */}
                <div className="mt-4 p-4 rounded-xl bg-violet-50 border border-violet-100">
                  <p className="text-sm text-violet-900 font-medium">
                    El 80% de los pacientes paga entre <strong className="tabular-nums">{formatCLP(resultado.p10)}</strong> y <strong className="tabular-nums">{formatCLP(resultado.p90)}</strong>
                  </p>
                </div>

                {/* Seguro complementario */}
                {resultado.pct_copago_0 > 0 && (
                  <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-emerald-800 font-medium">
                        {resultado.pct_copago_0.toFixed(1)}% de los pacientes pagó $0 (seguro complementario cubrió la diferencia)
                      </p>
                      {seguroComp && (
                        <p className="text-[11px] text-emerald-600 mt-1">
                          Con seguro complementario activo, su copago podría ser <strong>$0</strong> en esta prestación.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Metadata grid */}
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase">Observaciones</div>
                    <div className="text-sm font-bold text-slate-900 tabular-nums">{formatNum(resultado.n_total)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase">Dispersión (P75-P25)</div>
                    <div className="text-sm font-bold text-slate-900 tabular-nums">{formatCLP(resultado.p75 - resultado.p25)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase">ISAPRE</div>
                    <div className="text-xs font-medium text-slate-700">{resultado.isapre}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase">Copago $0</div>
                    <div className="text-sm font-bold text-slate-900 tabular-nums">{resultado.pct_copago_0}%</div>
                  </div>
                </div>
              </SectionCard>

              {/* Desglose */}
              <SectionCard title="Desglose de Costo" subtitle="Composición promedio del precio">
                <div className="mt-4 space-y-3">
                  {[
                    { label: "Precio clínica", value: resultado.avg_precio, color: "bg-slate-200", pct: 100 },
                    { label: "Bonificación ISAPRE", value: resultado.avg_bonif, color: "bg-blue-400", pct: resultado.avg_precio > 0 ? (resultado.avg_bonif / resultado.avg_precio * 100) : 0 },
                    { label: "Seguro complementario", value: resultado.avg_seg_compl || 0, color: "bg-emerald-400", pct: resultado.avg_precio > 0 ? ((resultado.avg_seg_compl || 0) / resultado.avg_precio * 100) : 0 },
                    { label: "Copago paciente (P50)", value: resultado.p50, color: "bg-violet-500", pct: resultado.avg_precio > 0 ? (resultado.p50 / resultado.avg_precio * 100) : 0 },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-36 text-xs text-slate-600">{item.label}</div>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} rounded-full`} style={{ width: `${Math.min(item.pct, 100)}%` }} />
                      </div>
                      <div className="w-28 text-right text-xs font-semibold text-slate-800 tabular-nums">
                        {formatCLP(item.value)} <span className="text-slate-400 font-normal">({item.pct.toFixed(0)}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Histograma */}
              <SectionCard title="Distribución de Copago" subtitle="Frecuencia estimada por rango (distribución teórica aproximada)">
                <BarChart
                  categories={["P0-P10", "P10-P25", "P25-P50", "P50-P75", "P75-P90", "P90+"]}
                  series={[{ name: "% pacientes", data: [10, 15, 25, 25, 15, 10] }]}
                  colors={["#8B5CF6"]}
                  height={200}
                  distributed
                />
              </SectionCard>

              {/* Nota metodológica */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="text-[11px] text-slate-500 leading-relaxed space-y-1">
                    <p><strong className="text-slate-700">Metodología:</strong> Se analizaron {formatNum(resultado.n_total)} bonos IMED para esta combinación de prestación + ISAPRE en los últimos 12 meses.</p>
                    <p><strong className="text-slate-700">Fórmula:</strong> copago_unitario = copago ÷ cantidad_prestaciones. Percentiles calculados sobre copago unitario.</p>
                    <p><strong className="text-slate-700">Seguro complementario:</strong> Cuando seg_compl &gt; 0, un seguro adicional cubrió parte o todo el copago residual. {resultado.pct_copago_0}% de los pacientes pagó $0.</p>
                    <p><strong className="text-slate-700">Fuente:</strong> reds_comercial.com_zimed_report_variante · Período: {COTIZADOR_META.periodo}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
